import React,{useEffect,useMemo,useState}from'react';
import{AnimatePresence,motion}from'framer-motion';
import*as FiIcons from'react-icons/fi';
import SafeIcon from'../common/SafeIcon';
import FormField from'./FormField';
import CeremonyMusicTable from'./CeremonyMusicTable';
import TimelineBuilder from'./TimelineBuilder';
import MusicManager from'./MusicManager';
import CandleLightingTable from'./CandleLightingTable';
import GrandEntranceTable from'./GrandEntranceTable';
import ClientPlannerHeader from'./ClientPlannerHeader';
import PlannerSidebar from'./PlannerSidebar';
import VenueLogisticsChecklist from'./VenueLogisticsChecklist';
import MissingAnswerAlert from'./MissingAnswerAlert';
import{initialTimeline}from'../data';
import{getClientQuestionnaireSections}from'../utils/questionnaireSections';
import'./client-planner.css';

const{
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCloud,
  FiSave
}=FiIcons;

const playlistCategories=[
  'Cocktail Hour',
  'Dinner',
  'Dancing',
  'Candle Lighting',
  'Do Not Play',
  'Other'
];

const specialMusicQuestionIds=[
  'songRequestsTable',
  'playlistLinks'
];

function isAnswered(value){
  if(Array.isArray(value)){
    return value.length>0;
  }

  if(value&&typeof value==='object'){
    return Array.isArray(value.selected)
      ?value.selected.length>0
      :Object.values(value).some((item)=>
        String(item||'').trim()
      );
  }

  return String(value||'').trim().length>0;
}

function getTimelineFallback(sections){
  const timelineSection=sections.find(
    (section)=>section.id==='timeline'
  );

  if(!timelineSection?.questions?.length){
    return initialTimeline;
  }

  return timelineSection.questions.map((question,index)=>({
    id:question.id||`timeline-${index}`,
    time:'',
    name:question.title,
    description:question.help||'',
    music:'',
    notes:''
  }));
}

function getAnswerStorageKey(event){
  return`amp-planner-answers-${event.id}`;
}

function getEventDefaults(event){
  return{
    date:event.date||'',
    guests:event.guests||'',
    venueName:event.venue||'',
    eventDescription:event.eventDescription||event.serviceDetails||'',
    eventDuration:event.eventDuration||'',
    eventStartTime:event.eventStartTime||'',
    setupStartTime:event.setupStartTime||'',
    eventEndTime:event.eventEndTime||'',
    honoree:
      event.type==='Bar Mitzvah'||
      event.type==='Bat Mitzvah'
        ?event.name||''
        :''
  };
}

function getInitialAnswers(event){
  const defaults=getEventDefaults(event);
  const stored=readLocalState(
    getAnswerStorageKey(event),
    null
  );

  return stored
    ?{...defaults,...stored}
    :defaults;
}

function normalizeQuestionTitle(title=''){
  return String(title)
    .toLowerCase()
    .replace(/[^\w\s]/g,'')
    .replace(/\s+/g,' ')
    .trim();
}

function removeDuplicateQuestions(questions){
  const seenIds=new Set();
  const seenTitles=new Set();

  return questions.filter((question)=>{
    const title=normalizeQuestionTitle(question.title);

    if(
      seenIds.has(question.id)||
      !title||
      seenTitles.has(title)
    ){
      return false;
    }

    seenIds.add(question.id);
    seenTitles.add(title);
    return true;
  });
}

function ReviewSummary({
  event,
  timeline,
  songs,
  playlists,
  warnings,
  onSubmit
}){
  return(
    <div className="planner-review">
      {warnings.length>0&&(
        <div className="planner-warning" role="alert">
          <strong>Recommended before submitting</strong>
          {warnings.map((warning)=>(
            <p key={warning}>• {warning}</p>
          ))}
        </div>
      )}

      <div className="review-grid">
        <article>
          <small>Event</small>
          <strong>{event.name}</strong>
          <span>{event.date} · {event.venue}</span>
        </article>

        <article>
          <small>Timeline</small>
          <strong>{timeline.length} moments</strong>
          <span>
            {timeline[0]?.time||'Time TBD'} through{' '}
            {timeline[timeline.length-1]?.time||'Time TBD'}
          </span>
        </article>

        <article>
          <small>Music</small>
          <strong>
            {songs.filter((song)=>song.title).length} songs
          </strong>
          <span>
            {playlists.filter((list)=>list.url).length} playlists provided
          </span>
        </article>

        <article>
          <small>Production</small>
          <strong>
            {event.services?.join(',')||'Services TBD'}
          </strong>
          <span>DJ: {event.dj||'TBD'}</span>
        </article>
      </div>

      <button
        type="button"
        className="button primary submit-button"
        onClick={onSubmit}
      >
        Submit event planner
        <SafeIcon icon={FiCheck}/>
      </button>
    </div>
  );
}

function ClientPlanner({sections,event}){
  const isWedding=event.type==='Wedding';
  const bookedServices=Array.isArray(event.services)
    ?event.services
    :[];

  const questionnaireSections=useMemo(
    ()=>getClientQuestionnaireSections(
      sections,
      event.type
    ),
    [sections,event.type]
  );

  const enabled=questionnaireSections.filter(
    (section)=>section.enabled
  );

  const answerStorageKey=getAnswerStorageKey(event);

  const[step,setStep]=useState(0);
  const[answers,setAnswers]=useState(
    ()=>getInitialAnswers(event)
  );
  const[timeline,setTimeline]=useState(
    ()=>readLocalState(
      `amp-timeline-${event.id}`,
      getTimelineFallback(questionnaireSections)
    )
  );
  const[songs,setSongs]=useState(
    ()=>readLocalState(
      `amp-songs-${event.id}`,
      [{id:1,title:'',artist:'',priority:'Must Play',notes:''}]
    )
  );
  const[playlists,setPlaylists]=useState(
    ()=>readLocalState(
      `amp-playlists-${event.id}`,
      playlistCategories.map((category)=>({
        category,
        url:''
      }))
    )
  );
  const[saved,setSaved]=useState(true);
  const[submitted,setSubmitted]=useState(false);
  const[missingAttempt,setMissingAttempt]=useState(false);

  const current=enabled[step];

  const completion=Math.min(
    100,
    Math.round(
      ((step+Object.keys(answers).length/8)/enabled.length)*100
    )
  );

  const visibleQuestions=useMemo(()=>{
    if(
      !current||
      current.id==='timeline'||
      current.id==='candle-lighting'
    ){
      return[];
    }

    const questions=current.questions.filter((question)=>{
      if(specialMusicQuestionIds.includes(question.id)){
        return false;
      }

      if(question.id==='venueRequirements'){
        return false;
      }

      if(question.id==='ceremonies'&&!isWedding){
        return false;
      }

      if(question.id==='couple'&&!isWedding){
        return false;
      }

      if(
        question.id==='introductions'&&
        current.id==='reception'
      ){
        return false;
      }

      return true;
    });

    return current.id==='music'
      ?removeDuplicateQuestions(questions)
      :questions;
  },[current,isWedding]);

  const musicQuestions=current?.id==='music'
    ?current.questions.filter((question)=>
      specialMusicQuestionIds.includes(question.id)
    )
    :[];

  const missingRequired=useMemo(
    ()=>visibleQuestions.filter(
      (question)=>
        question.required&&
        !isAnswered(answers[question.id])
    ),
    [visibleQuestions,answers]
  );

  const warnings=useMemo(()=>{
    const items=[];

    if(!answers.ceremonyTime&&isWedding){
      items.push('Your ceremony start time is missing.');
    }

    if(
      answers.additionalDances==='Yes'&&
      !answers.additionalDanceDetails
    ){
      items.push(
        'Add details and music for your additional dances.'
      );
    }

    return items;
  },[answers,isWedding]);

  useEffect(()=>{
    const timer=setTimeout(()=>{
      localStorage.setItem(
        answerStorageKey,
        JSON.stringify(answers)
      );
      localStorage.setItem(
        `amp-timeline-${event.id}`,
        JSON.stringify(timeline)
      );
      localStorage.setItem(
        `amp-songs-${event.id}`,
        JSON.stringify(songs)
      );
      localStorage.setItem(
        `amp-playlists-${event.id}`,
        JSON.stringify(playlists)
      );
      setSaved(true);
    },500);

    return()=>clearTimeout(timer);
  },[
    answers,
    timeline,
    songs,
    playlists,
    answerStorageKey,
    event.id
  ]);

  const update=(id,value)=>{
    setSaved(false);
    setMissingAttempt(false);

    setAnswers((currentAnswers)=>{
      if(value?.selected){
        return{
          ...currentAnswers,
          [id]:value.selected,
          ...(id==='mitzvahGames'
            ?{
              mitzvahGamesOtherDetails:
                value.otherDetails||''
            }
            :{}),
          ...(id==='lineDances'
            ?{
              lineDancesOtherDetails:
                value.otherDetails||''
            }
            :{}),
          ...(id==='mitzvahLineDancing'
            ?{
              mitzvahLineDancingOtherDetails:
                value.otherDetails||''
            }
            :{})
        };
      }

      return{
        ...currentAnswers,
        [id]:value
      };
    });
  };

  const continueNext=()=>{
    if(missingRequired.length){
      setMissingAttempt(true);
      return;
    }

    setStep((value)=>value+1);
    setMissingAttempt(false);
  };

  if(submitted){
    return(
      <main className="client-shell client-success-screen">
        <div className="success-icon">
          <SafeIcon icon={FiCheck}/>
        </div>

        <span className="eyebrow">Planner submitted</span>
        <h1>Thank you,{event.client.split(' ')[0]}!</h1>
        <p>
          Your event information has been submitted to Angels Music
          Productions. Our DJ and production team will review every
          detail and follow up if needed.
        </p>

        <div className="contact-card">
          <strong>Questions?</strong>
          <a href="tel:9493942572">949-394-2572</a>
          <a href="mailto:info@angelsmusic.net">
            info@angelsmusic.net
          </a>
        </div>
      </main>
    );
  }

  if(!current){
    return null;
  }

  return(
    <main className="client-shell">
      <ClientPlannerHeader
        event={event}
        completion={completion}
        saved={saved}
      />

      <div
        className="planner-progress-bar"
        aria-label={`${completion}% complete`}
      >
        <span style={{width:`${completion}%`}}/>
      </div>

      <div className="planner-layout">
        <PlannerSidebar
          sections={enabled}
          step={step}
          setStep={setStep}
        />

        <section className="planner-card">
          <div className="planner-card-topline">
            <span>Step {step+1} of {enabled.length}</span>
            <span>
              {current.id==='timeline'
                ?`${timeline.length} moments`
                :current.id==='candle-lighting'
                  ?`${answers.candleLightingRows?.length||13} candles`
                  :current.id==='ceremony'&&
                    event.type==='Wedding'
                    ?'5 rows'
                    :current.id==='reception'&&
                      event.type!=='Wedding'
                      ?`${visibleQuestions.length+1} questions`
                      :`${visibleQuestions.length} questions`}
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              className="planner-step-content"
              initial={{opacity:0,y:12}}
              animate={{opacity:1,y:0}}
              exit={{opacity:0,y:-12}}
            >
              <div className="planner-heading">
                <span className="eyebrow">
                  Section {String(step+1).padStart(2,'0')}
                </span>
                <h2>{current.title}</h2>
                <p>{current.description}</p>
              </div>

              {current.id==='welcome'&&(
                <div className="welcome-banner">
                  <strong>Welcome to your event planner.</strong>
                  <p>
                    Complete this at your own pace. Your booking details
                    are already included,and every change is saved
                    automatically on this device.
                  </p>
                </div>
              )}

              {missingAttempt&&(
                <MissingAnswerAlert questions={missingRequired}/>
              )}

              {current.id!=='timeline'&&
                current.id!=='ceremony'&&
                current.id!=='reception'&&
                current.id!=='candle-lighting'&&
                current.id!=='music'&&(
                  <div className="dynamic-form">
                    {visibleQuestions.map((item)=>(
                      <FormField
                        key={item.id}
                        question={item}
                        value={answers[item.id]}
                        otherDetails={
                          answers[`${item.id}OtherDetails`]
                        }
                        invalid={
                          missingAttempt&&
                          item.required&&
                          !isAnswered(answers[item.id])
                        }
                        onChange={(value)=>update(item.id,value)}
                        serviceDetails={event.serviceDetails}
                        bookedServices={bookedServices}
                      />
                    ))}
                  </div>
                )}

              {current.id==='reception'&&event.type!=='Wedding'&&(
                <>
                  <GrandEntranceTable
                    eventType={event.type}
                    answers={answers}
                    onChange={update}
                  />

                  <div className="dynamic-form">
                    {visibleQuestions.map((item)=>(
                      <FormField
                        key={item.id}
                        question={item}
                        value={answers[item.id]}
                        otherDetails={
                          answers[`${item.id}OtherDetails`]
                        }
                        invalid={
                          missingAttempt&&
                          item.required&&
                          !isAnswered(answers[item.id])
                        }
                        onChange={(value)=>update(item.id,value)}
                        serviceDetails={event.serviceDetails}
                        bookedServices={bookedServices}
                      />
                    ))}
                  </div>
                </>
              )}

              {current.id==='ceremony'&&isWedding&&(
                <CeremonyMusicTable
                  questions={visibleQuestions}
                  answers={answers}
                  onChange={update}
                />
              )}

              {current.id==='ceremony'&&event.type!=='Wedding'&&(
                <GrandEntranceTable
                  eventType={event.type}
                  answers={answers}
                  onChange={update}
                />
              )}

              {current.id==='venue'&&(
                <VenueLogisticsChecklist
                  answers={answers}
                  onChange={update}
                />
              )}

              {current.id==='timeline'&&(
                <TimelineBuilder
                  items={timeline}
                  setItems={setTimeline}
                />
              )}

              {current.id==='music'&&(
                <>
                  <div className="dynamic-form">
                    {visibleQuestions.map((item)=>(
                      <FormField
                        key={item.id}
                        question={item}
                        value={answers[item.id]}
                        otherDetails={
                          answers[`${item.id}OtherDetails`]
                        }
                        invalid={
                          missingAttempt&&
                          item.required&&
                          !isAnswered(answers[item.id])
                        }
                        onChange={(value)=>update(item.id,value)}
                        serviceDetails={event.serviceDetails}
                        bookedServices={bookedServices}
                      />
                    ))}
                  </div>

                  <MusicManager
                    songs={songs}
                    setSongs={setSongs}
                    playlists={playlists}
                    setPlaylists={setPlaylists}
                    questions={musicQuestions}
                  />
                </>
              )}

              {current.id==='candle-lighting'&&(
                <CandleLightingTable
                  answers={answers}
                  onChange={update}
                />
              )}

              {current.id==='review'&&(
                <ReviewSummary
                  event={event}
                  timeline={timeline}
                  songs={songs}
                  playlists={playlists}
                  warnings={warnings}
                  onSubmit={()=>setSubmitted(true)}
                />
              )}

              <footer className="planner-footer">
                <button
                  type="button"
                  className="button text-button"
                  disabled={step===0}
                  onClick={()=>setStep((value)=>value-1)}
                >
                  <SafeIcon icon={FiArrowLeft}/>
                  Back
                </button>

                <span className={`save-state ${saved?'saved':''}`}>
                  <SafeIcon icon={saved?FiCloud:FiSave}/>
                  {saved?'Saved':'Saving...'}
                </span>

                {step<enabled.length-1&&(
                  <button
                    type="button"
                    className="button primary"
                    onClick={continueNext}
                  >
                    Continue
                    <SafeIcon icon={FiArrowRight}/>
                  </button>
                )}
              </footer>

              <div data-greta-form-placeholder="EVENT_PLANNER">
                This form is not collecting yet.
              </div>
            </motion.div>
          </AnimatePresence>
        </section>
      </div>
    </main>
  );
}

function readLocalState(key,fallback){
  try{
    const value=localStorage.getItem(key);
    return value?JSON.parse(value):fallback;
  }catch{
    return fallback;
  }
}

export default ClientPlanner;